import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Switch,
  Textarea,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  Select,
  SelectItem,
  Slider,
  Chip,
  Divider,
  Badge,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Tabs,
  Tab,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@nextui-org/react";
import { FaEye, FaCode, FaCopy, FaChevronDown } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faColumns, faExclamationTriangle, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import SubheadingField from "./InputField/SubheadingField";
import ConditionalField from "./InputField/ConditionalField";
import { groupFormDataBySubheadings } from "../../utils/formGrouping.utils";
import { StandardCancelButton, StandardSubmitButton, StandardCloseButton, StandardCopyButton } from "../../components/StandardButtons";

const getInitialState = (arr) => {
  const data = {};
  arr?.forEach((item) => {
    // Skip subheading fields as they don't need form data
    if (item[1].type === "subheading") {
      return;
    }
    
    if (item[1].type === "boolean") {
      data[item[0]] = "false";
    } else if (item[1].type === "slider") {
      data[item[0]] = item[1].value || 0;
    } else if (item[1].type === "file") {
      data[item[0]] = [];
    } else if (item[1].type === "metricSelector") {
      data[item[0]] = "";
    } else {
      data[item[0]] = "";
    }
  });
  return data;
};

const EnhancedPreviewForm = ({ data, fieldInfoList = [] }) => {
  const [formdata, setFormData] = useState({});
  const [formSchema, setFormSchema] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const [showJSON, setShowJSON] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [layoutMode, setLayoutMode] = useState("2-column"); // 2-column, 1-column

  const requiredArr = data?.JsonSchema?.required || [];

  const changeHandler = (title, value, type) => {
    if (type === "boolean") {
      if (value === "") {
        setFormData({ ...formdata, [title]: "false" });
      } else {
        setFormData({ ...formdata, [title]: value });
      }
    } else {
      setFormData({ ...formdata, [title]: value });
    }
  };

  useEffect(() => {
    const responseData = data?.JsonSchema?.properties;
    const fieldOrder = data?.JsonSchema?.fieldOrder || [];
    
    if (responseData) {
      let responseArr = [];
      
      // Use saved field order if available
      if (fieldOrder.length > 0) {
        // Process fields in the saved order
        fieldOrder.forEach(key => {
          if (responseData[key]) {
            responseArr.push([key, responseData[key]]);
          }
        });
        
        // Add any fields not in the order list (backwards compatibility)
        Object.keys(responseData).forEach(key => {
          if (!fieldOrder.includes(key)) {
            responseArr.push([key, responseData[key]]);
          }
        });
      } else {
        // Fallback: use displayOrder or id for backwards compatibility
        responseArr = Object.keys(responseData).map((key) => [
          key,
          responseData[key],
        ]);
        
        responseArr.sort((a, b) => {
          if (a[1].displayOrder !== undefined && b[1].displayOrder !== undefined) {
            return a[1].displayOrder - b[1].displayOrder;
          }
          // Fallback to id-based sorting for old data
          return a[1].id - b[1].id;
        });
      }
      
      setFormSchema(responseArr);
      setFormData(getInitialState(responseArr));
    }
  }, [data]);



  const renderField = (fieldKey, fieldConfig, index) => {
    const fieldError = validationErrors[fieldKey];
    const isRequired = fieldConfig?.required || requiredArr.includes(fieldKey);
    const fieldValue = formdata[fieldKey] || "";

    // Add key prop for better React performance
    const key = `${fieldKey}-${index}`;

    if (fieldConfig.type === "subheading") {
      return (
        <SubheadingField
          key={key}
          field={{
            ...fieldConfig,
            title: fieldKey
          }}
        />
      );
    }

    if (fieldConfig.type === "conditional") {
      return (
        <ConditionalField
          key={key}
          field={{
            ...fieldConfig,
            Title: fieldKey
          }}
          formData={formdata}
          onChange={changeHandler}
          existingFields={formSchema}
        />
      );
    }

    const baseFieldProps = {
      isRequired,
      variant: "faded",
      labelPlacement: "outside",
      className: "mb-6",
      isInvalid: fieldError && isRequired,
      errorMessage: fieldError,
    };

    // Add validation styling
    const validationClass = isRequired ? "border-emerald-200 focus:border-emerald-500" : "";
    const labelClass = isRequired ? "text-gray-900" : "text-gray-700";

    switch (fieldConfig.type?.toLowerCase()) {
      case "text":
      case "string":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Input
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              type="text"
              placeholder={`Enter ${fieldConfig.title || fieldKey}`}
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Textarea
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              placeholder={`Enter ${fieldConfig.title || fieldKey}`}
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
              rows={4}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "metricSelector":
        return (
          <div key={`${fieldKey}-${index}`} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            
            <Button
              variant="bordered"
              className="w-full justify-between"
              isDisabled={true}
            >
              <span className="text-gray-500">Select Metric</span>
              <FaChevronDown />
            </Button>
            
            {/* Show category filter information */}
            {fieldConfig.metricCategoryFilter && Array.isArray(fieldConfig.metricCategoryFilter) && fieldConfig.metricCategoryFilter.length > 0 ? (
              <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-xs text-emerald-800">
                  <span className="font-medium">Category Filter:</span> 
                  <div className="mt-1 flex flex-wrap gap-1">
                    {fieldConfig.metricCategoryFilter.map((category, idx) => (
                      <Chip key={idx} size="sm" color="success" variant="flat" className="text-xs">
                        {category}
                      </Chip>
                    ))}
                  </div>
                  <div className="mt-2 text-emerald-700">
                    Users will see metrics from {fieldConfig.metricCategoryFilter.length} selected categories
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-xs text-emerald-800">
                  <span className="font-medium">No Category Filter:</span> 
                  <div className="mt-1 text-emerald-700">
                    Users will see all available metrics
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-2 text-xs text-gray-500">
              Preview Mode - Click to open metric browser modal
            </div>
          </div>
        );
      case "integer":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Input
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              type="number"
              placeholder="Enter number"
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
              min={fieldConfig.min_val}
              max={fieldConfig.max_val}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "password":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Input
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              type="password"
              placeholder="Enter password"
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "dropdown":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Select
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              placeholder="Select an option"
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
            >
              {fieldConfig.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </Select>
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "boolean":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Switch
              isSelected={fieldValue === "true"}
              onValueChange={(value) => changeHandler(fieldKey, value.toString(), fieldConfig.type)}
              className="mb-2"
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "slider":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}: {fieldValue}%
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Slider
              size="md"
              step={fieldConfig.step_val || 1}
              minValue={fieldConfig.min_val || 0}
              maxValue={fieldConfig.max_val || 100}
              value={Number(fieldValue) || 0}
              onChange={(value) => changeHandler(fieldKey, value, fieldConfig.type)}
              className="max-w-md"
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "file":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <input
              type="file"
              multiple={fieldConfig.isMultiSelect}
              accept={fieldConfig.acceptFieldType?.join(",")}
              onChange={(e) => changeHandler(fieldKey, Array.from(e.target.files), fieldConfig.type)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      case "date":
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <DatePicker
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              onChange={(date) => changeHandler(fieldKey, date, fieldConfig.type)}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div key={key} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <label className={`block text-sm font-medium ${labelClass}`}>
                {fieldConfig.title || fieldKey}
              </label>
              {isRequired && (
                <Chip size="sm" color="danger" variant="flat">
                  Required
                </Chip>
              )}
            </div>
            <Input
              key={key}
              isRequired={baseFieldProps.isRequired}
              variant={baseFieldProps.variant}
              labelPlacement={baseFieldProps.labelPlacement}
              className={`${baseFieldProps.className} ${validationClass}`}
              isInvalid={baseFieldProps.isInvalid}
              errorMessage={baseFieldProps.errorMessage}
              type="text"
              placeholder={`Enter ${fieldConfig.title || fieldKey}`}
              value={fieldValue}
              onChange={(e) => changeHandler(fieldKey, e.target.value, fieldConfig.type)}
            />
            {isRequired && (
              <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500" />
                <span>This field is required and will be validated</span>
              </div>
            )}
          </div>
        );
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    });
  };

  const groupedData = groupFormDataBySubheadings(formdata, formSchema);

  return (
    <div className="w-full">
      {/* Preview Controls - Compact */}
      <div className="mb-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaEye className="text-emerald-600" />
            <span className="font-semibold text-gray-700">Form Preview</span>

          </div>
          
          <div className="flex items-center gap-2">
            <Tooltip content="View form data and schema">
              <Button
                size="sm"
                variant="bordered"
                onPress={onOpen}
                startContent={<FaCode />}
                className="border-emerald-300 hover:border-emerald-400 text-emerald-700"
              >
                View Data & Schema
              </Button>
            </Tooltip>
          </div>
        </div>


      </div>

      {/* Form Preview */}
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-lg border border-emerald-200">
          <CardHeader className="flex flex-col gap-2 px-6 py-4 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-200">
            <h1 className="text-2xl font-bold text-center text-gray-800">
              {data?.Name || "Form Preview"}
            </h1>
            {data?.description && (
              <p className="text-sm text-gray-600 text-center max-w-3xl">
                {data.description}
              </p>
            )}
            <Divider className="mt-3" />
          </CardHeader>
          
          <CardBody className="px-6 py-6">
            {/* Layout Toggle */}
            <div className="flex items-center justify-center gap-3 mb-6 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <span className="text-sm text-gray-700 font-medium">Preview Layout:</span>
              <Button
                size="sm"
                variant={layoutMode === "2-column" ? "solid" : "bordered"}
                color={layoutMode === "2-column" ? "primary" : "default"}
                onPress={() => setLayoutMode("2-column")}
                startContent={<FontAwesomeIcon icon={faColumns} />}
                className="px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                2-Column
              </Button>
              <Button
                size="sm"
                variant={layoutMode === "1-column" ? "solid" : "bordered"}
                color={layoutMode === "1-column" ? "primary" : "default"}
                onPress={() => setLayoutMode("1-column")}
                startContent={<FontAwesomeIcon icon={faColumns} />}
                className="px-4"
              >
                1-Column
              </Button>
            </div>

            {/* Form Fields */}
            <div className={layoutMode === "2-column" ? "grid grid-cols-2 gap-6" : "space-y-4"}>
              {formSchema.map(([fieldKey, fieldConfig], index) => 
                renderField(fieldKey, fieldConfig, index)
              )}
            </div>

            {/* Form Actions */}
            <div className="mt-8 pt-6 border-t border-emerald-200">
              <div className="flex justify-end gap-3">
                <StandardCancelButton className="px-6">
                  Cancel
                </StandardCancelButton>
                <StandardSubmitButton className="px-6">
                  Submit Form
                </StandardSubmitButton>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Data Modal */}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="4xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-200">
                <FaCode className="text-emerald-600" />
                <span className="text-xl font-semibold text-gray-800">Form Data & Schema</span>
              </ModalHeader>
              <ModalBody className="p-0">
                <Tabs aria-label="Form data and schema" className="w-full">
                  <Tab key="data" title="Form Data">
                    <div className="p-6">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-400">Current form values</span>
                          <StandardCopyButton
                            size="sm"
                            startContent={<FaCopy />}
                            onPress={() => copyToClipboard(JSON.stringify(formdata, null, 2))}
                            className="text-emerald-400 hover:text-emerald-300"
                          >
                            {copiedToClipboard ? "Copied!" : "Copy"}
                          </StandardCopyButton>
                        </div>
                        <pre className="text-sm">
                          {JSON.stringify(formdata, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </Tab>
                  <Tab key="schema" title="Form Schema">
                    <div className="p-6">
                      <div className="bg-gray-900 text-emerald-400 p-4 rounded-lg overflow-auto">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-400">Form structure definition</span>
                          <StandardCopyButton
                            size="sm"
                            startContent={<FaCopy />}
                            onPress={() => copyToClipboard(JSON.stringify(data?.JsonSchema, null, 2))}
                            className="text-emerald-400 hover:text-emerald-300"
                          >
                            {copiedToClipboard ? "Copied!" : "Copy"}
                          </StandardCopyButton>
                        </div>
                        <pre className="text-sm">
                          {JSON.stringify(data?.JsonSchema, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </ModalBody>
              <ModalFooter>
                <StandardCloseButton onPress={onClose}>
                  Close
                </StandardCloseButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default EnhancedPreviewForm;